import * as React from "react";
import { Box, Button, Menu, MenuItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLanguage, useTranslation } from "../i18n/LanguageContext";
import { useAuth } from "../authContext/authContext";
import { updateMyProfile } from "../Services/apiService";

// Compact "current language + dropdown" control, fixed to the top-right
// corner. Not a full-width bar — a single small button, per design decision.
// z-index sits below MUI's modal layer (1300) so dialogs still overlay it.
//
// Logged-in users get a save-or-just-for-now prompt on change, since this
// control otherwise applies instantly with no separate "Save" step (unlike
// the Settings dialog's language field, which is already save-gated by its
// own Save Changes button). Logged-out contexts (auth screens, and the
// maintenance page, which renders with no AuthProvider at all) skip the
// prompt entirely and keep today's instant-apply behavior.
function LanguageSwitcher({ sx }) {
  const { language, setLanguage, languages } = useLanguage();
  const { t } = useTranslation();
  const auth = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingLang, setPendingLang] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const isAuthenticated = Boolean(auth?.isAuthenticated);

  const handleSelect = (lang) => {
    setAnchorEl(null);
    if (lang === language) return;
    if (isAuthenticated) {
      setPendingLang(lang);
    } else {
      setLanguage(lang);
    }
  };

  const closePrompt = () => setPendingLang(null);

  const applyJustForNow = () => {
    setLanguage(pendingLang);
    closePrompt();
  };

  const applyAndSave = async () => {
    const target = languages.find((l) => l.code === pendingLang);
    if (!target?.id) {
      // Fallback list (API list hasn't loaded) has no real id to save yet —
      // still apply it locally rather than silently doing nothing.
      applyJustForNow();
      return;
    }
    setSaving(true);
    try {
      const res = await updateMyProfile({ languageId: target.id });
      auth?.updateUserFields?.({
        languageId: res.data?.language_id,
        languageCode: res.data?.language_code,
        languageName: res.data?.language_name,
      });
    } catch {
      // Save failed — still apply the pick for this session rather than
      // stranding the user on the language they just chose not to keep.
    } finally {
      setSaving(false);
      setLanguage(pendingLang);
      closePrompt();
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 1200,
        ...sx,
      }}
    >
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        size="small"
        sx={{
          minWidth: 0,
          px: 1.5,
          py: 0.5,
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "uppercase",
          color: "#374151",
          bgcolor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 5,
          boxShadow: "0 2px 6px rgba(15,23,42,0.08)",
          "&:hover": { bgcolor: "#f9fafb" },
        }}
      >
        {language}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {languages.map((lang) => (
          <MenuItem key={lang.code} selected={lang.code === language} onClick={() => handleSelect(lang.code)}>
            <ListItemText primary={lang.native_name || lang.name} />
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={Boolean(pendingLang)} onClose={closePrompt} maxWidth="xs" fullWidth>
        <DialogTitle>{t("languageSwitcher.promptTitle")}</DialogTitle>
        <DialogContent>{t("languageSwitcher.promptBody")}</DialogContent>
        <DialogActions>
          <Button onClick={applyJustForNow} disabled={saving}>
            {t("languageSwitcher.justForNow")}
          </Button>
          <Button onClick={applyAndSave} variant="contained" disabled={saving}>
            {t("languageSwitcher.saveForNextTime")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LanguageSwitcher;
